// src/components/SummaryControls.jsx
import React from "react";

// Example groups
const STUDENT_GROUPS = [
  "All Students",
  "American Indian/Alaskan Native",
  "Asian",
  "Black/African American",
  "ELL",
  "Female",
  "Female or Gender X",
  "FRL",
  "Gender X",
  "Hispanic/Latino of any race(s)",
  "Homeless",
  "Male",
  "Male or Gender X",
  "Migrant",
  "Military Parent",
  "Native Hawaiian/Other Pacific Islander",
  "Non Military Parent",
  "Non-ELL",
  "Non-FRL",
  "Non-Homeless",
  "Non-Migrant",
  "Non-Section504",
  "Section504",
  "Student with Disabilities",
  "Student without Disabilities",
  "Two or More Races",
  "White",
];

/**
 * Sticky bar with the controls that apply to every chart on the page:
 * School, Student Group, Comparison Group.
 *
 * Positioning note: the page scrolls inside <main>, which already starts below
 * the fixed navbar, so the bar sticks at top-0 of that scroll container.
 */
export default function SummaryControls({
  schools,
  selectedSchool,
  onSchoolChange,
  selectedGroup,
  onGroupChange,
  selectedComparisonGroup,
  onComparisonChange,
}) {
  return (
    <div className="sticky top-0 z-40 w-full bg-white border-b border-gray-300 shadow-sm">
      <div className="max-w-[1200px] mx-auto px-8 py-2 flex flex-wrap gap-x-6 gap-y-2 items-center text-sm">
        <div>
          <label className="mr-2 font-semibold">School:</label>
          <select
            className="border rounded px-2 py-1"
            value={selectedSchool}
            onChange={(e) => onSchoolChange(e.target.value)}
          >
            {schools.map((sch) => (
              <option key={sch} value={sch}>
                {sch}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mr-2 font-semibold">Group:</label>
          <select
            className="border rounded px-2 py-1"
            value={selectedGroup}
            onChange={(e) => onGroupChange(e.target.value)}
          >
            {STUDENT_GROUPS.map((grp) => (
              <option key={grp} value={grp}>
                {grp}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mr-2 font-semibold">Compare to:</label>
          <select
            className="border rounded px-2 py-1"
            value={selectedComparisonGroup}
            onChange={(e) => onComparisonChange(e.target.value)}
          >
            {["none", ...STUDENT_GROUPS].map((g) => (
              <option key={g} value={g}>
                {g === "none" ? "No comparison" : g}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/** Year picker. Only the cluster charts are year-specific, so it lives in that section. */
export function YearSelect({ selectedYear, onYearChange, startYear = 2020, yearCount = 5 }) {
  const years = Array.from({ length: yearCount }, (_, i) => startYear + i);
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <span className="font-semibold text-gray-700">Year:</span>
      <select
        className="border rounded px-2 py-1 bg-white"
        value={selectedYear}
        onChange={(e) => onYearChange(Number(e.target.value))}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Segmented control for the concentrator denominator. Lives next to the chart it affects. */
export function DenominatorToggle({ value, onChange }) {
  const options = [
    { key: "totalStudents", label: "All students" },
    { key: "totalParticipants", label: "CTE participants" },
  ];
  return (
    <div className="inline-flex items-center gap-2 text-sm">
      <span className="text-gray-600">Concentrators as % of:</span>
      <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="radiogroup">
        {options.map((opt) => {
          const active = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.key)}
              className={`px-3 py-1 transition-colors ${
                active
                  ? "bg-gray-800 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
